import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  BL_BODY_OPERATOR_FIELDS,
  type EditState,
  type ReviewSystemValues,
} from './invoice-review-overrides';

// Shown when a field's value differs from the system resolution: one click
// puts the resolved value back, which clears the override on save. This is the
// explicit replacement for the old blank-means-system convention.
function ResetToSystem({
  label,
  current,
  system,
  onReset,
}: Readonly<{ label: string; current: string; system: string; onReset: () => void }>) {
  if (current.trim() === system) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-auto px-1 py-0.5 text-xs text-muted-foreground"
      aria-label={`Reset ${label} to the system value`}
      onClick={onReset}
    >
      Reset to system value
    </Button>
  );
}

export interface ReviewBodyFieldsProps {
  readonly edit: EditState;
  readonly setEdit: (next: EditState) => void;
  readonly system: ReviewSystemValues;
  readonly disabled: boolean;
}

// The material description, prefilled with what the document renders today.
function DescriptionField({ edit, setEdit, system, disabled }: Readonly<ReviewBodyFieldsProps>) {
  const systemValue = system['description_of_goods'] ?? '';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="review-description">Material description</Label>
        <ResetToSystem
          label="Material description"
          current={edit.description}
          system={systemValue}
          onReset={() => {
            setEdit({ ...edit, description: systemValue });
          }}
        />
      </div>
      <Textarea
        id="review-description"
        rows={2}
        disabled={disabled}
        value={edit.description}
        onChange={(e) => {
          setEdit({ ...edit, description: e.target.value });
        }}
      />
    </div>
  );
}

// The operator-supplied BL-body fields as single-line inputs, each prefilled
// with the value the document renders today (the override where one is stored,
// else the system resolution — an em-dash field starts empty). Only a value
// that differs from the system resolution is written as an override on save.
export function ReviewBodyFields(props: Readonly<ReviewBodyFieldsProps>) {
  const { edit, setEdit, system, disabled } = props;
  return (
    <>
      <DescriptionField {...props} />
      {BL_BODY_OPERATOR_FIELDS.map(({ key, label }) => (
        <div key={key} className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor={`review-${key}`}>{label}</Label>
            <ResetToSystem
              label={label}
              current={edit.operatorFields[key] ?? ''}
              system={system[key] ?? ''}
              onReset={() => {
                setEdit({
                  ...edit,
                  operatorFields: { ...edit.operatorFields, [key]: system[key] ?? '' },
                });
              }}
            />
          </div>
          <Input
            id={`review-${key}`}
            placeholder="Prints as — until filled"
            disabled={disabled}
            value={edit.operatorFields[key] ?? ''}
            onChange={(e) => {
              setEdit({
                ...edit,
                operatorFields: { ...edit.operatorFields, [key]: e.target.value },
              });
            }}
          />
        </div>
      ))}
    </>
  );
}
